import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import DragDropContainer from 'editor/components/drag-drop-container';
import eq from 'editor/helpers/eq';
import gt from 'editor/helpers/gt';
import htmlId from 'editor/helpers/html-id';
import not from 'editor/helpers/not';
import or from 'editor/helpers/or';
import pick from 'editor/helpers/pick';
import queue from 'editor/helpers/queue';
import set from 'editor/helpers/set';
import type { Instrument } from 'editor/models/drumset';
import Drumset from 'editor/models/drumset';
import { PartType, SectionType, SongPart } from 'editor/models/song';
import {
  AddAccentCommand,
  ImportAccentCommand,
  RandomiseFillsCommand,
  RemoveAccentCommand,
  SortFillsCommand,
  UpdateAccentVolumeCommand,
  SetAutoPilotLoopCommand,
  SetAutoPilotOutroPlayAfterCommand,
  UpdatePlayAtCommand,
  UpdatePlayForCommand,
  SetAutoPilotPerPartCommand,
} from 'editor/models/song-commands';
import disabled from 'editor/modifiers/disabled';
import onClickOutside from 'editor/modifiers/on-click-outside';
import selected from 'editor/modifiers/selected';
import type PasteboardService from 'editor/services/pasteboard';
import ProjectManagerService, {
  DrumsetFile,
  EffectFile,
} from 'editor/services/project-manager';
import SongPlayerService from 'editor/services/song-player';
import UndoManagerService from 'editor/services/undo-manager';
import constrainValue from 'editor/utils/constrain-value';
import playInstrument, {
  getGainNodeForVelocityRandom,
} from 'editor/utils/play-instrument';
import velcro from 'ember-velcro/modifiers/velcro';
import Button from '../button';
import MenuButton from '../menu-button';
import OptionButton from '../option-button';
import SelectableItem from '../selectable-item';
import sortable from 'editor/modifiers/sortable';


interface BuildAccentWindowSignature {
  Element: HTMLDivElement;
  Args: {
    // eslint-disable-next-line no-unused-vars
    importAccent: (file: File) => void;
    close: () => void;
  };
  Blocks: {
    // default: [];
  };
}

// Refactor: This shares a lot of code with the instrument import component
class BuildAccentWindow extends Component<BuildAccentWindowSignature> {
  @service declare projectManager: ProjectManagerService;

  audioContext: AudioContext;
  drumsetVolume: GainNode;

  @tracked selectedDrumset?: DrumsetFile;
  @tracked instrumentsLoading = false;
  @tracked selectedInstruments: Instrument[] = [];
  @tracked velocity: number = 127;
  @tracked _name?: string;

  constructor(owner: unknown, args: BuildAccentWindowSignature['Args']) {
    super(owner, args);

    this.audioContext = new AudioContext({
      latencyHint: 'interactive',
      sampleRate: 44100,
    });
    this.drumsetVolume = new GainNode(this.audioContext, { gain: 1 });
    this.drumsetVolume.connect(this.audioContext.destination);
  }

  @action
  async selectDrumset(drumsetFile: DrumsetFile) {
    this.selectedDrumset = drumsetFile;
    this.instrumentsLoading = true;
    await drumsetFile.parse().then((drumset) => {
      if (drumset) {
        drumset.prepareAudio(this.audioContext);
      }
    });

    this.instrumentsLoading = false;
  }

  get availableInstruments() {
    const drumsetPlayableInstruments =
      this.selectedDrumset?.drumset?.playableInstruments || [];
    return drumsetPlayableInstruments.filter(
      (i) =>
        this.selectedInstruments.includes(i) ||
        !this.selectedInstruments.map((i) => i.id).includes(i.id),
    );
  }

  @action
  isSelectedInstrument(instrument: Instrument) {
    return this.selectedInstruments.includes(instrument);
  }

  @action
  toggleInstrument(instrument: Instrument, ev?: MouseEvent) {
    if (ev?.shiftKey) {
      const firstSelectedInstrument = this.selectedInstruments.at(0);
      const lastSelectedInstrument = this.selectedInstruments.at(-1);
      if (firstSelectedInstrument && lastSelectedInstrument) {
        const firstIndex = this.availableInstruments.indexOf(firstSelectedInstrument);
        const lastIndex = this.availableInstruments.indexOf(lastSelectedInstrument);
        const currentIndex = this.availableInstruments.indexOf(instrument);
        if (currentIndex < firstIndex) {
          this.selectedInstruments = [
            ...this.selectedInstruments,
            ...this.availableInstruments.slice(currentIndex, firstIndex + 1),
          ];
        } else if (currentIndex > lastIndex) {
          this.selectedInstruments = [
            ...this.selectedInstruments,
            ...this.availableInstruments.slice(lastIndex, currentIndex + 1),
          ];
        } else {
          this.selectedInstruments = [
            ...this.selectedInstruments,
            ...this.availableInstruments.slice(firstIndex, currentIndex + 1),
          ];
        }
      } else {
        this.selectedInstruments = [instrument];
      }
    } else {
      if (this.isSelectedInstrument(instrument)) {
        this.selectedInstruments = this.selectedInstruments.filter(
          (i) => i != instrument,
        );
      } else {
        this.selectedInstruments = [...this.selectedInstruments, instrument];
      }
    }
    // remove window selection
    window.getSelection()?.removeAllRanges();
  }

  @action
  setVelocity(value: string) {
    this.velocity = constrainValue(parseInt(value, 10), 1, 127);
  }

  get name() {
    return (
      this._name ??
      [
        this.selectedDrumset?.name,
        ...this.selectedInstruments.map((i) => i.name),
        this.velocity,
      ].join(':')
    );
  }

  @action
  setName(name: string) {
    this._name = name;
  }

  @action
  playAccent() {
    this.audioContext.resume();
    this.selectedInstruments.forEach((instrument) => {
      playInstrument(
        this.audioContext,
        this.drumsetVolume,
        instrument,
        0,
        0,
        this.velocity,
      );
    });
  }

  @action
  async addAccent() {
    // Calculate the total time from the longest instrument sample
    const totalTime = Math.max(
      ...this.selectedInstruments.map((instrument) => {
        let [, sample] = getGainNodeForVelocityRandom(
          this.audioContext,
          this.drumsetVolume,
          instrument,
          this.velocity,
        );
        if (sample) {
          return sample.nSample / sample.nChannel;
        }
        return 0;
      }),
    );

    const audioContext = new OfflineAudioContext({
      numberOfChannels: 2,
      length: Math.ceil(totalTime / 2),
      sampleRate: 44100,
    });
    const gainNode = new GainNode(audioContext, { gain: 1 });
    gainNode.connect(audioContext.destination);

    this.selectedInstruments.forEach((instrument) => {
      playInstrument(audioContext, gainNode, instrument, 0, 0, this.velocity);
    });
    const audioBuffer = await audioContext.startRendering();
    const fileData = Drumset.saveWaveFile(audioBuffer);
    this.args.importAccent(
      new File([fileData], `${this.name}.wav`, { type: 'audio/wav' }),
    );
  }

  <template>
    <div class="p-4" ...attributes>
      <div
        class="flex flex-col h-full min-h-0 gap-2 overflow-auto"
        data-test-instrument-import
      >
        <h1 class="font-bold text-lg">Build an accent</h1>
        <p>Select a drumset and instruments to build an accent (e.g. Bass drum and crash
          cymbal)</p>
        <div class="flex gap-2 min-h-0 overflow-auto h-full">
          <div class="overflow-auto border-r dark:border-primary-darkBorderColor">
            <div data-test-drumsets class="flex flex-col gap-px h-max">
              {{#each this.projectManager.drumsets as |drumset|}}
                <SelectableItem
                  @icon="drum"
                  @model={{drumset}}
                  @field="name"
                  @when={{false}}
                  title={{drumset.name}}
                  {{selected when=(eq this.selectedDrumset drumset)}}
                  {{on "click" (fn this.selectDrumset drumset)}}
                >{{drumset.name}}</SelectableItem>
              {{/each}}
            </div>
          </div>
          <div class="overflow-auto">
            <div data-test-instruments class="flex flex-col gap-px h-max">
              {{#if this.instrumentsLoading}}
                <div>Loading...</div>
              {{else}}
                {{#each this.availableInstruments as |instrument|}}
                  <SelectableItem
                    @model={{instrument}}
                    @field="name"
                    @when={{false}}
                    title={{instrument.name}}
                    {{selected when=(this.isSelectedInstrument instrument)}}
                    {{on "click" (fn this.toggleInstrument instrument)}}
                  >{{instrument.id}}. {{instrument.name}}</SelectableItem>

                {{else}}
                  <em>Select a drumset to view instruments</em>
                {{/each}}
              {{/if}}
            </div>
          </div>
        </div>
        <div class="flex gap-4 w-full justify-between">
          <label class="flex items-center gap-2">Velocity
            <input
              type="range"
              min="1"
              max="127"
              value={{this.velocity}}
              class="accent-primary w-60"
              {{on "input" (pick "target.value" this.setVelocity)}}
            />
          </label>
          <label class="flex gap-4 items-center">Name:
            <input
              type="text"
              value={{this.name}}
              {{on "input" (pick "target.value" this.setName)}}
              class="w-full bg-white border rounded-md dark:bg-primary-darkFill dark:border-primary-darkBorderColor"
            /></label>
          <div>
            <Button
              class="dark:text-white"
              @icon="xmark"
              title="Cancel"
              {{on "click" (fn this.args.close) }}
            />
            <Button
              {{disabled when=(eq 0 this.selectedInstruments.length)}}
              @icon="play"
              {{on "click" this.playAccent}}
            />
            <Button
              {{disabled when=(eq 0 this.selectedInstruments.length)}}
              class="bg-primary text-white"
              {{on "click" this.addAccent}}
            >Add accent</Button>
          </div>
        </div>
      </div>
    </div>
  </template>
}

interface AddAccentButtonSignature {
  Element: HTMLDivElement;
  Args: {
    // eslint-disable-next-line no-unused-vars
    addAccent: (effect: EffectFile) => void;
    // eslint-disable-next-line no-unused-vars
    importAccent: (files: FileList | File[]) => void;
    // eslint-disable-next-line no-unused-vars
    playAccent: (part: SongPart) => void;
  };
  Blocks: {
    // default: [];
  };
}

class AddAccentButton extends Component<AddAccentButtonSignature> {
  @service declare projectManager: ProjectManagerService;
  @service declare songPlayer: SongPlayerService;
  @service declare undoManager: UndoManagerService;

  @tracked showAccents = false;
  @tracked showBuildAccentWindow = false;

  elementId = guidFor(this);

  get sortedEffects() {
    return this.projectManager.effects.toSorted((e1, e2) =>
      e1.name.localeCompare(e2.name),
    );
  }

  @action
  toggleShowAccents() {
    this.showAccents = !this.showAccents;
  }

  @action
  toggleShowBuildAccentWindow() {
    this.showBuildAccentWindow = !this.showBuildAccentWindow;
  }

  @action
  addAccent(effect: EffectFile) {
    this.args.addAccent(effect);
  }

  @action
  importAccent(file: File) {
    this.args.importAccent([file]);
  }

  @action
  playEffect(effect: EffectFile) {
    this.songPlayer.playAccent(effect, 100);
  }

  @action
  popupMiddleware() {
    return [
      // Calculates the maximum height for the accent popup list
      {
        name: 'Max height',
        options: {},
        async fn(state: any) {
          const availableHeight = document.documentElement.clientHeight;
          const top = state.rects.reference.y + state.rects.reference.height;
          const height = availableHeight - top - 20;
          state.elements.floating.style.maxHeight = `${height}px`;
          return state;
        },
      },
    ];
  }

  <template>
    <div class="w-full">
      <Button
        @icon="plus"
        id={{this.elementId}}
        class="w-full h-full"
        title="Select an accent for this part"
        {{on "click" this.toggleShowAccents}}
      >
        accent
      </Button>
      {{#if this.showAccents}}
        <div
          class="absolute z-40 flex flex-col overflow-auto bg-white border rounded shadow-lg dark:bg-primary-darkFill dark:text-white dark:border-primary-darkBorderColor"
          {{velcro (htmlId this.elementId) middleware=(this.popupMiddleware)}}
          {{onClickOutside
            (queue
              (set this "showBuildAccentWindow" false) (set this "showAccents" false)
            )
          }}
        >
          {{#if this.showBuildAccentWindow}}
            <BuildAccentWindow
              @importAccent={{this.importAccent}}
              @close={{this.toggleShowBuildAccentWindow}}
              class="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-white border rounded shadow-lg dark:bg-primary-darkFill dark:text-white dark:border-primary-darkBorderColor"
            />
          {{/if}}
          <div
            class="flex gap-1 p-1 bg-white border-t first-of-type:border-none hover:bg-gray-100 dark:bg-primary-darkFill dark:text-white dark:border-primary-darkBorderColor dark:hover:bg-primary-darkButtonSelected"
          >
            <Button
              @icon="drum"
              class="px-2 py-1 text-left text-sm grow border-0"
              {{on "click" this.toggleShowBuildAccentWindow}}
            >Build an accent</Button>
          </div>
          {{#each this.sortedEffects as |effect|}}
            <div
              class="group flex items-start justify-center gap-1 p-1 bg-white border-t first-of-type:border-none hover:bg-gray-100 dark:bg-primary-darkFill dark:text-white dark:border-primary-darkBorderColor dark:hover:bg-primary-darkButtonSelected"
            >
              <Button
                @icon="play"
                class="px-2 py-1 m-auto bg-white hover:bg-primary"
                {{on "click" (fn this.playEffect effect)}}
              />
              <MenuButton
                type="button"
                class="px-2 py-1 text-left text-sm grow !border-b-0 dark:bg-primary-darkFill dark:group-hover:bg-primary-darkButtonSelected dark:group-hover:text-black"
                {{on "click" (fn this.addAccent effect)}}
              >
                {{effect.name}}
              </MenuButton>
            </div>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

interface VolumeButtonSignature {
  Element: HTMLDivElement;
  Args: {
    part: SongPart;
    // eslint-disable-next-line no-unused-vars
    playAccent: (part: SongPart) => void;
  };
  Blocks: {
    // default: [];
  };
}

class VolumeButton extends Component<VolumeButtonSignature> {
  @service declare undoManager: UndoManagerService;

  @tracked showVolume = false;

  get part() {
    return this.args.part;
  }

  get volumeIcon() {
    if (this.part.accentVolume < 90) {
      return 'volume-low';
    } else if (this.part.accentVolume > 110) {
      return 'volume-high';
    } else {
      return 'volume';
    }
  }

  @action
  resetVolume() {
    this.changeVolume(100);
  }

  @action
  async changeVolume(volume: number) {
    await this.undoManager.executeCommand(
      new UpdateAccentVolumeCommand(this.part, volume),
    );
    this.args.playAccent(this.part);
  }

  @action
  toggleShowVolume(ev: Event) {
    ev.stopPropagation();
    this.showVolume = !this.showVolume;
  }

  @action
  noop(ev: Event) {
    ev.stopPropagation();
    ev.stopImmediatePropagation();
  }

  <template>
    <label
      class="relative flex items-center px-2 py-1 border rounded dark:border-primary-darkBorderColor"
      {{on "dblclick" this.resetVolume}}
      {{on "click" this.toggleShowVolume}}
      {{onClickOutside (set this "showVolume" false)}}
    >
      <FaIcon @icon={{this.volumeIcon}} @fixedWidth={{true}} />
      {{#if this.showVolume}}
        <div class="absolute z-50 -rotate-90 -translate-y-1/2 top-1/2">
          <input
            type="range"
            min="0"
            max="200"
            class="w-20 accent-primary"
            value={{@part.accentVolume}}
            {{on "click" this.noop}}
            {{on "change" (pick "target.value" this.changeVolume)}}
          />
        </div>
      {{/if}}
    </label>
  </template>
}

interface SongPlayerPartSignature {
  Element: HTMLDivElement;
  Args: {
    type: PartType;
    part: SongPart;
    name: string;
    // eslint-disable-next-line no-unused-vars
    addPart?: (ev: MouseEvent) => void;
    // eslint-disable-next-line no-unused-vars
    removePart?: (part: SongPart, ev: MouseEvent) => void;
    // eslint-disable-next-line no-unused-vars
    duplicatePart?: (part: SongPart, ev: MouseEvent) => void;
    // eslint-disable-next-line no-unused-vars
    pastePart?: (part: SongPart, ev: MouseEvent) => void;
    // eslint-disable-next-line no-unused-vars
    addTrack: (sectionType: SectionType) => void;
    // eslint-disable-next-line no-unused-vars
    selectTrack: (sectionType: SectionType, index: number) => void;
    // eslint-disable-next-line no-unused-vars
    importMidi: (sectionType: SectionType, index: number, files: File[]) => void;
    // eslint-disable-next-line no-unused-vars
    importMultiMidi: (sectionType: SectionType, index: number, files: File[]) => void;
  };
  Blocks: {
    // default: [];
  };
}

export default class SongPlayerPartComponent extends Component<SongPlayerPartSignature> {
  @service declare songPlayer: SongPlayerService;
  @service declare projectManager: ProjectManagerService;
  @service declare undoManager: UndoManagerService;
  @service declare pasteboard: PasteboardService;

  @tracked showMenu = false;
  @tracked showFillMenu = false;
  @tracked showAccentMenu = false;
  @tracked selectedTrackType = SectionType.MAIN;
  @tracked selectedFillIndex = 0;

  constructor(owner: unknown, args: SongPlayerPartSignature['Args']) {
    super(owner, args);
  }

  elementId = guidFor(this);

  get part(): SongPart {
    return this.args.part;
  }

  get partNumber() {
    const match = this.args.name.match(/part (\d+)/i);
    return match ? parseInt(match[1], 10) : null;
  }

  get isLastPart() {
    return this.partNumber === this.songPlayer._currentSong?.parts.length;
  }

  get isLastPartAPEnabled() {
    return this.songPlayer._currentSong?.parts[this.songPlayer._currentSong?.parts.length - 1].info.autoPilotEnabled;
  }

  get blurOutroSetting() {
    return !this.isLastPartAPEnabled || this.isAutoPilotLoop || (this.songPlayer._currentSong?.info.outro.mainLoopFileIndex == -1);
  }

  get partSupportsFills() {
    return this.args.type === PartType.PART;
  }

  get canAddFill() {
    return this.partSupportsFills && this.part.fills.length < 8;
  }

  get showPlayAtText() {
    return this.partSupportsFills && this.part.fills.length < 3;
  }

  get isAutoPilotEnabled() {
    return this.songPlayer._currentSong?.autoPilotEnabled && this.projectManager.isProjectDRMX;
  }

  get showLoopSong() {
    return this.isAutoPilotEnabled && this.isLastPart;
  }

  get isAutoPilotLoop() {
    return this.songPlayer._currentSong?.autoPilotLoop;
  }

  get isAutoPilotOutroPlayAfter() {
    return !this.isAutoPilotLoop && this.songPlayer._currentSong?.autoPilotOutroPlayAfter && (this.songPlayer._currentSong?.info.outro.mainLoopFileIndex != -1);
  }

  get canAddMain() {
    return !this.part.main;
  }

  get partSupportsTransition() {
    return this.args.type === PartType.PART;
  }

  get canAddTransition() {
    return this.partSupportsTransition && !this.part.transition;
  }

  get partSupportsAccents() {
    return this.args.type === PartType.PART;
  }

  get canAddAccent() {
    return this.partSupportsAccents && !this.part.hasAccent;
  }

  @action
  accentName(part: SongPart) {
    return this.projectManager.findEffect(part.accentFileName)?.name;
  }

  @action
  async playAccent(part: SongPart) {
    const accent = this.projectManager.findEffect(part.accentFileName);
    if (accent) {
      this.songPlayer.playAccent(accent, part.accentVolume);
    }
  }

  @action
  removeAccent(part: SongPart) {
    this.undoManager.executeCommand(new RemoveAccentCommand(part));
  }

  @action
  toggleShuffleFills() {
    this.undoManager.executeCommand(
      new RandomiseFillsCommand(this.part, !this.part.shuffleFills),
    );
  }

  get hasCopiedPart() {
    return this.pasteboard.hasCopy('part');
  }

  get hasCopiedAccent() {
    return this.pasteboard.hasCopy('accent');
  }

  @action
  copyPart(part: SongPart) {
    this.pasteboard.store('part', part);
  }

  @action
  copyAccent(part: SongPart) {
    const accent = this.projectManager.findEffect(part.accentFileName);
    this.pasteboard.store('accent', accent);
  }

  @action
  pasteAccent(part: SongPart) {
    const loadedAccent = this.projectManager.findEffect(part.accentFileName);
    const accent = this.pasteboard.retrieve('accent');

    if (!loadedAccent) {
      this.undoManager.executeCommand(new AddAccentCommand(this.part, accent.path));
    } else {
      this.removeAccent(part);
      this.undoManager.executeCommand(new AddAccentCommand(this.part, accent.path));
    }
  }

  partType(type: PartType) {
    switch (type) {
      case PartType.INTRO:
        return 'intro';
      case PartType.OUTRO:
        return 'outro';
      case PartType.PART:
        return 'part';
      default:
        throw new Error('Unknown part type');
    }
  }
  @action
  disableShuffleFills(part: SongPart) {
    if (!this.songPlayer!._currentSong?.autoPilotEnabled) {
      return false;
    }
    return part.info.autoPilotEnabled;
  }

  @action
  addAccent(effectFile: EffectFile) {
    this.undoManager.executeCommand(new AddAccentCommand(this.part, effectFile.path));
  }

  @action
  importAccent(files: FileList | File[]) {
    if (files.length > 0) {
      const file = files[0]!;
      if (file.type !== 'audio/wav') {
        console.error(`Invalid file type for accent: "${file.name}" has type "${file.type}", expected "audio/wav"`);
        return;
      }
      const command = new ImportAccentCommand(this.projectManager, file);
      command.on('execute', (effectFile) => {
        this.undoManager.appendCommand(new AddAccentCommand(this.part, effectFile.path));
      });
      this.undoManager.executeCommand(command);
    }
  }

  @action
  importMidi(sectionType: SectionType, index: number, files: FileList) {
    const midiFiles = Array.from(files).filter(
      (file) => file.type === 'audio/midi' || file.type === 'audio/mid',
    );
    if (midiFiles.length === 0) {
      const fileNames = Array.from(files).map(f => f.name).join(', ');
      console.error(`No MIDI files found in selection. Files provided: [${fileNames}]`);
      return;
    }
    this.args.importMidi(sectionType, index, midiFiles);
  }

  @action
  toggleAutoPilotPerPart() {
    this.undoManager.executeCommand(
      new SetAutoPilotPerPartCommand(this.part, !this.part.info.autoPilotEnabled),
    );
  }

  @action
  trackSelected(sectionType: SectionType, index: number) {
    this.selectedTrackType = sectionType;
    this.selectedFillIndex = index;
    this.args.selectTrack(sectionType, index);
  }

  @action
  drumFillPlayAt(index: number) {
    return this.part.info.drumFillPlayAt[index];
  }


  @action
  changePlayAt(fillIndex: number, ev: Event) {
    const input = ev.target as HTMLInputElement;
    const playAt = parseInt(input.value);
    if (isNaN(playAt)) { input.value = String(this.part.info.drumFillPlayAt[fillIndex]); return; }
    this.undoManager.executeCommand(
      new UpdatePlayAtCommand(
        this.part,
        fillIndex,
        playAt,
      ),
    );
  }

  @action
  changePlayFor(sectionType: SectionType, ev: Event) {
    const input = ev.target as HTMLInputElement;
    const playFor = parseInt(input.value);
    if (isNaN(playFor)) { input.value = String(this.part.info.playFor); return; }
    this.undoManager.executeCommand(
      new UpdatePlayForCommand(this.part, sectionType, playFor),
    );
  }

  @action
  toggleAutoPilotLoop() {
    const autopilotLoopVal = !this.isAutoPilotLoop;
    this.undoManager.executeCommand(
      new SetAutoPilotLoopCommand(this.songPlayer._currentSong, autopilotLoopVal),
    );
  }

  @action
  toggleAutoPilotOutroPlayAfter() {
    if (this.isAutoPilotLoop) {
      return;
    }
    const autopilotOutroPlayAfterVal = !this.isAutoPilotOutroPlayAfter;
    this.undoManager.executeCommand(
      new SetAutoPilotOutroPlayAfterCommand(
        this.songPlayer._currentSong,
        autopilotOutroPlayAfterVal,
      ),
    );
  }

  @action
  importMidis(sectionType: SectionType, index: number) {
    this.args.importMultiMidi(sectionType, index, []);
  }

  @action
  sortFills(oldIndex: number, newIndex: number) {
    this.undoManager.executeCommand(new SortFillsCommand(this.part, oldIndex, newIndex));
  }

  <template>
    <div
      class="grid items-stretch w-full col-span-6 grid-cols-subgrid part dark:bg-primary-darkFill"
      data-part-type={{this.partType @part.type}}
      ...attributes
    >
      <div class="flex items-center font-bold">
        {{#if (eq @part.type PartType.PART)}}<span
            class=" drag-handle opacity-30"
          ></span>
          {{else}}<span class="w-5 -ml-2"></span>{{/if}}
          <div class="flex-col">
        {{@name}}
{{#if (eq @part.type PartType.PART)}}
        {{#if this.isAutoPilotEnabled}}
          <div class="flex items-center gap-1 whitespace-nowrap">
            <span class="text-xs bg-white text-black px-1 py-1 dark:bg-[#262930] dark:text-gray-300">AP</span>
            <div class="toggle-switch-partAP">
              <input
                type="checkbox"
                id={{this.elementId}}
                checked={{this.part.info.autoPilotEnabled}}
                {{on "change" this.toggleAutoPilotPerPart}}
              />
              <label for={{this.elementId}}></label>
            </div>
          </div>
          {{/if}}
          {{/if}}
        </div>
      </div>
      <div class="flex-col gap-4">
        <DragDropContainer
          @onDrop={{fn this.importMidi SectionType.MAIN 0}}
          class="flex border border-transparent border-dashed droppable:!border-green-500 dark:border-primary-darkBorderColor"
        >
          {{#if @part.main}}
            <button
              type="button"
              class="w-full px-2 py-1 overflow-hidden text-sm border rounded text-nowrap text-ellipsis bg-primary-light selected:bg-primary selected:text-white dark:bg-primary-darkSample dark:border-primary-darkBorderColor"
              title={{@part.main.name}}
              {{selected when=(eq this.songPlayer.track @part.main)}}
              {{on "click" (fn this.trackSelected SectionType.MAIN 0)}}
            >
              {{@part.main.name}}
            </button>
          {{/if}}
          {{#if this.canAddMain}}
            <Button
              @icon="plus"
              title="Add track"
              class="w-full"
              {{on "click" (fn @addTrack SectionType.MAIN)}}
            >track</Button>
          {{/if}}
        </DragDropContainer>
        {{#if this.isAutoPilotEnabled}}
        {{#if (eq @part.type PartType.PART)}}
        <div class="flex flex-row w-full mt-2 items-center gap-3 px-2 border-2 border-dotted border-[#6FBCC3] rounded bg-gray-100 dark:bg-primary-darkComponent flex-nowrap overflow-x-auto">

              <label class="items-center gap-2 flex flex-nowrap">
                <span
                  class="{{if (not this.part.info.autoPilotEnabled) "blur-on-disable"}} text-xs bg-white text-black px-1 py-1.5 dark:bg-[#262930] dark:text-gray-300"
                >Play For</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  inputmode="numeric"
                  value={{this.part.info.playFor}}
                  autocomplete="off"
                  onwheel="this.blur()"
                  oninput="this.value = this.value.replace(/\D+/g, '')"
                  disabled={{not this.part.info.autoPilotEnabled}}
                  title={{if
                    (not this.part.info.autoPilotEnabled)
                    "Turn autopilot on for this part to edit"
                    ""
                  }}
                  {{on "change" (fn this.changePlayFor this.selectedTrackType)}}
                  class="{{if (not this.part.info.autoPilotEnabled) "blur-on-disable"}} w-10 no-spinner text-center border rounded bg-white text-black dark:bg-primary-darkComponent dark:text-gray-300 dark:border-primary-darkBorderColor"
                />
              </label>
            </div>
          {{/if}}
          {{#if (eq @part.type PartType.OUTRO)}}
            <label
              class="flex items-center gap-2 mt-2 px-2 border-2 border-dotted border-[#6FBCC3] w-full"
            >
              <span
                class="{{if this.blurOutroSetting "blur-on-disable"}} text-xs bg-white dark:bg-[#262930] px-1 py-1.5 text-black dark:text-gray-300"
              >Play After</span>
              <input
                type="checkbox"
                class="{{if this.blurOutroSetting "blur-on-disable"}} form-checkbox w-4 h-4 accent-primary dark:accent-primary-darkBorderColor"
                checked={{this.isAutoPilotOutroPlayAfter}}
                disabled={{this.blurOutroSetting}}
                {{on "change" this.toggleAutoPilotOutroPlayAfter}}
              />
            </label>
          {{/if}}
        {{/if}}
      </div>
      <DragDropContainer
        @onDrop={{fn this.importMidi SectionType.FILL @part.fills.length}}
        @isActive={{this.canAddFill}}
        class="flex justify-end gap-2 border border-transparent border-dashed droppable:!border-green-500"
      >
        <div
          {{sortable draggable=".fills" sort=true onChange=(fn this.sortFills)}}
          class="flex flex-nowrap overflow-x-auto w-full gap-2"
        >
          {{#each @part.fills as |fill i|}}

            {{#if this.isAutoPilotEnabled}}
             <div class="flex-col">
            <div class="fills flex w-full gap-2 overflow-hidden">
              <button
                type="button"
                class="w-full px-2 py-1 overflow-hidden text-sm border rounded text-nowrap text-ellipsis bg-primary-light selected:bg-primary selected:text-white dark:bg-primary-darkSample dark:border-primary-darkBorderColor"
                title={{fill.name}}
                {{selected when=(eq this.songPlayer.track fill)}}
                {{on "click" (fn this.trackSelected SectionType.FILL i)}}
              >
                {{fill.name}}
              </button>
            </div>
              <label class="flex items-center gap-2 mt-2 border-2 border-dotted border-[#6FBCC3] rounded overflow-hidden">
                {{#if this.showPlayAtText}}
                  <span class="{{if (not this.part.info.autoPilotEnabled) "blur-on-disable"}} text-xs bg-white text-black px-1 py-1.5 dark:bg-[#262930] dark:text-gray-300">Play at</span>
                {{/if}}
                <input
                  type="number"
                  min="1"
                  step="1"
                  inputmode="numeric"
                  autocomplete="off"
                  onwheel="this.blur()"
                  oninput="this.value = this.value.replace(/\D+/g, '')"
                  disabled={{not this.part.info.autoPilotEnabled}}
                  title={{if (not this.part.info.autoPilotEnabled) "Turn autopilot on for this part to edit" ""}}
                  value={{this.drumFillPlayAt i}}
                  class="{{if (not this.part.info.autoPilotEnabled) "blur-on-disable"}} w-10 no-spinner text-center border rounded bg-white text-black dark:bg-primary-darkComponent dark:text-gray-300 dark:border-primary-darkBorderColor"
                  {{on "change" (fn this.changePlayAt i)}}
                />
              </label>
          </div>
          {{else}}
             <div class="fills flex w-full gap-2 overflow-hidden">
              <button
                type="button"
                class="w-full px-2 py-1 overflow-hidden text-sm border rounded text-nowrap text-ellipsis bg-primary-light selected:bg-primary selected:text-white dark:bg-primary-darkSample dark:border-primary-darkBorderColor"
                title={{fill.name}}
                {{selected when=(eq this.songPlayer.track fill)}}
                {{on "click" (fn this.trackSelected SectionType.FILL i)}}
              >
                {{fill.name}}
              </button>
            </div>
          {{/if}}

          {{/each}}
          {{#if (gt @part.fills.length 1)}}
            <Button
              @icon="shuffle"
              title="Randomise fills"
              {{selected when=@part.shuffleFills}}
              {{on "click" this.toggleShuffleFills}}
              {{disabled when=(this.disableShuffleFills @part)}}
            />
          {{/if}}
          {{#if this.canAddFill}}
            {{#if (eq @part.fills.length 0)}}
              <div class="flex justify-center w-full">
                <Button
                  @icon="plus"
                  disabled={{eq @part.fills.length 8}}
                  {{on "click" (fn @addTrack SectionType.FILL)}}
                />
                <Button
                  @icon="folder-open"
                  disabled={{eq @part.fills.length 8}}
                  {{on "click" (fn this.importMidis SectionType.FILL @part.fills.length)}}
                />
              </div>
            {{else}}
              <Button
                @icon="plus"
                disabled={{eq @part.fills.length 8}}
                {{on "click" (fn @addTrack SectionType.FILL)}}
              />
              <Button
                @icon="folder-open"
                disabled={{eq @part.fills.length 8}}
                {{on "click" (fn this.importMidis SectionType.FILL @part.fills.length)}}
              />
            {{/if}}
          {{/if}}
        </div>
      </DragDropContainer>
      <DragDropContainer
        @onDrop={{fn this.importMidi SectionType.TRANSITION 0}}
        @isActive={{this.partSupportsTransition}}
        class="flex border border-transparent border-dashed droppable:!border-green-500"
      >
        {{#if this.showLoopSong}}
        <div class="flex-col">
          {{#if @part.transition}}
            <button
              type="button"
              class="w-full px-2 py-1 overflow-hidden text-sm border rounded text-nowrap text-ellipsis btn bg-primary-light selected:bg-primary selected:text-white dark:bg-primary-darkSample dark:border-primary-darkBorderColor"
              title={{@part.transition.name}}
              {{selected when=(eq this.songPlayer.track @part.transition)}}
              {{on "click" (fn this.trackSelected SectionType.TRANSITION 0)}}
            >
              {{@part.transition.name}}
            </button>
          {{/if}}
          {{#if this.canAddTransition}}
            <Button
              @icon="plus"
              title="Add transition"
              class="w-full"
              {{on "click" (fn @addTrack SectionType.TRANSITION)}}
            >transition</Button>
          {{/if}}
          {{#if this.isLastPart}}
          <label class="flex items-center gap-2 mt-2 px-2 border-2 border-dotted border-[#6FBCC3] w-full">
            <span class="{{if (not this.part.info.autoPilotEnabled) "blur-on-disable"}} text-xs bg-white text-black px-1 py-1.5 dark:bg-[#262930] dark:text-gray-300">Loop Song</span>
            <input
              type="checkbox"
              class="{{if (not this.part.info.autoPilotEnabled) "blur-on-disable"}} form-checkbox w-4 h-4 accent-primary dark:accent-primary-darkBorderColor"
              disabled={{not this.part.info.autoPilotEnabled}}
              checked={{this.isAutoPilotLoop}}
              {{on "change" this.toggleAutoPilotLoop}}
          />
          </label>
          {{/if}}
          </div>
        {{else}}
        {{#if @part.transition}}
          <button
            type="button"
            class="w-full px-2 py-1 overflow-hidden text-sm border rounded text-nowrap text-ellipsis btn bg-primary-light selected:bg-primary selected:text-white dark:bg-primary-darkSample dark:border-primary-darkBorderColor"
            title={{@part.transition.name}}
            {{selected when=(eq this.songPlayer.track @part.transition)}}
            {{on "click" (fn this.trackSelected SectionType.TRANSITION 0)}}
          >
            {{@part.transition.name}}
          </button>
        {{/if}}
        {{#if this.canAddTransition}}
          <Button
            @icon="plus"
            title="Add transition"
            class="w-full"
            {{on "click" (fn @addTrack SectionType.TRANSITION)}}
          >transition</Button>
        {{/if}}
        {{/if}}
      </DragDropContainer>
      <DragDropContainer
        @onDrop={{this.importAccent}}
        @isActive={{this.partSupportsAccents}}
        class="flex gap-2 border border-dashed border-transparent droppable:!border-green-500"
      >
        {{#if @part.hasAccent}}
          <Button
            @icon="play"
            title={{this.accentName @part}}
            {{on "click" (fn this.playAccent @part)}}
          />
          <VolumeButton @part={{@part}} @playAccent={{this.playAccent}} />
          <div
            class="relative flex items-center border rounded dark:border-primary-darkBorderColor selected:bg-primary selected:text-white hover:bg-gray-100 dark:hover:bg-primary-darkButtonSelected"
          >
            <OptionButton
              id={{concat "accent-menu-" this.elementId}}
              class="relative px-4"
              {{selected when=this.showAccentMenu}}
              {{on "click" (set this "showAccentMenu" true)}}
            />
            {{#if this.showAccentMenu}}
              <nav
                class="absolute z-20 flex flex-col bg-white border rounded shadow-lg dark:border-primary-darkBorderColor"
                {{velcro
                  (htmlId (concat "accent-menu-" this.elementId))
                  placement="left-start"
                }}
                {{onClickOutside (set this "showAccentMenu" false)}}
              >
                <MenuButton
                  @icon="copy"
                  {{on
                    "click"
                    (queue (fn this.copyAccent @part) (set this "showAccentMenu" false))
                  }}
                >Copy</MenuButton>
                <MenuButton
                  @icon="paste"
                  {{disabled when=(not this.hasCopiedAccent)}}
                  {{on
                    "click"
                    (queue (fn this.pasteAccent @part) (set this "showAccentMenu" false))
                  }}
                >Paste</MenuButton>
                <MenuButton
                  @icon="trash-can"
                  class="px-2 py-1 text-left hover:bg-gray-100"
                  {{on
                    "click"
                    (queue (fn this.removeAccent @part) (set this "showAccentMenu" false))
                  }}
                >Delete</MenuButton>
              </nav>
            {{/if}}
          </div>
        {{else if this.canAddAccent}}
          <AddAccentButton
            @addAccent={{this.addAccent}}
            @importAccent={{this.importAccent}}
            @playAccent={{this.playAccent}}
          />
          <div
            class="relative border rounded dark:border-primary-darkBorderColor selected:bg-primary selected:text-white hover:bg-gray-100 dark:hover:bg-primary-darkButtonSelected"
          >
            <OptionButton
              id={{concat "accent-menu-" this.elementId}}
              class="relative px-4"
              {{selected when=this.showAccentMenu}}
              {{on "click" (set this "showAccentMenu" true)}}
            />
            {{#if this.showAccentMenu}}
              <nav
                class="absolute z-20 flex flex-col bg-white border rounded shadow-lg dark:border-primary-darkBorderColor"
                {{velcro
                  (htmlId (concat "accent-menu-" this.elementId))
                  placement="left-start"
                }}
                {{onClickOutside (set this "showAccentMenu" false)}}
              >
                <MenuButton
                  @icon="paste"
                  {{disabled when=(not this.hasCopiedAccent)}}
                  {{on
                    "click"
                    (queue (fn this.pasteAccent @part) (set this "showAccentMenu" false))
                  }}
                >Paste</MenuButton>
              </nav>
            {{/if}}
          </div>
        {{/if}}
      </DragDropContainer>
      <div class="flex gap-2">
        {{#if @addPart}}
          <Button @icon="plus" {{on "click" @addPart}} />
        {{/if}}
        {{#if (or @removePart @duplicatePart @pastePart)}}
          <div class="relative">
            <OptionButton
              id={{concat "part-menu-" this.elementId}}
              class="border dark:border-primary-darkBorderColor rounded selected:bg-primary selected:text-white"
              {{selected when=this.showMenu}}
              {{on "click" (set this "showMenu" true)}}
            />
            {{#if this.showMenu}}
              <nav
                class="absolute z-20 flex flex-col bg-white border rounded dark:border-primary-darkBorderColor shadow-lg"
                {{velcro
                  (htmlId (concat "part-menu-" this.elementId))
                  placement="left-start"
                }}
                {{onClickOutside (set this "showMenu" false)}}
              >
                <MenuButton
                  @icon="copy"
                  {{on
                    "click"
                    (queue (fn this.copyPart @part) (set this "showMenu" false))
                  }}
                >Copy</MenuButton>
                {{#if @pastePart}}
                  <MenuButton
                    @icon="paste"
                    {{disabled when=(not this.hasCopiedPart)}}
                    {{on
                      "click"
                      (queue (fn @pastePart @part) (set this "showMenu" false))
                    }}
                  >Paste</MenuButton>
                {{/if}}
                {{#if @duplicatePart}}
                  <MenuButton
                    @icon="clone"
                    {{on
                      "click"
                      (queue (fn @duplicatePart @part) (set this "showMenu" false))
                    }}
                  >Duplicate</MenuButton>
                {{/if}}
                {{#if @removePart}}
                  <MenuButton
                    @icon="trash-can"
                    class="px-2 py-1 text-left hover:bg-gray-100"
                    {{on
                      "click"
                      (queue (fn @removePart @part) (set this "showMenu" false))
                    }}
                  >Delete</MenuButton>
                {{/if}}
              </nav>
            {{/if}}
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
